import { CreditNoteStatus } from '@/features/credit-notes/constants'
import { calcCreditNoteTotals } from '@/features/credit-notes/utils/totals'
import type {
  CreditNote,
  CreditNoteCharge,
  CreateCreditNoteRequest,
  CreateCreditNoteResponse,
  UpdateCreditNoteRequest,
  GetAssetDetailsResponse,
  CreditNoteCustomer 
} from '@/api/billing/credit-note-schemas'
import {
  getCreditNoteById,
  createCreditNote,
  updateCreditNote,
  approveCreditNoteById,
  deleteCreditNoteItemById
} from '@/api/billing/credit-notes'

const db = new Map<number, CreditNote>()
let seq = 10000

function cloneNote(note: CreditNote): CreditNote {
  return {
    ...note,
    owner: { ...note.owner },
    customer: { ...note.customer },
    charges: (note.charges ?? []).map(c => ({ ...c })),
    date: note.date ? new Date(note.date) : new Date()
  }
}

function mapBackendStatus(status: string | null | undefined): CreditNoteStatus {
  switch (status) {
    case CreditNoteStatus.Approved:
      return CreditNoteStatus.Approved
    case CreditNoteStatus.Sent:
      return CreditNoteStatus.Sent
    case CreditNoteStatus.Cancelled:
      return CreditNoteStatus.Cancelled
    default:
      // backend sends "ActionRequired" etc -> treat as Draft in UI
      return CreditNoteStatus.Draft
  }
}

function calc(charges: CreditNoteCharge[], vatPercentage: number) {
  const { subTotalCents, vatCents, totalCents } = calcCreditNoteTotals(charges, vatPercentage)
  return {
    subTotalInFractionalCurrency: subTotalCents,
    vatInFractionalCurrency: vatCents,
    totalInFractionalCurrency: totalCents
  }
}

function applyBackendToDraft(note: CreditNote, backend: CreateCreditNoteResponse) {
  note.backendId = backend.id
  note.creditNoteNumber = backend.creditNoteReference
  note.date = new Date(backend.date)
  note.status = mapBackendStatus(backend.status)
  note.notes = backend.notes ?? note.notes ?? ''
  note.vatPercentage = backend.vatPercentage

  note.charges = backend.creditNoteItems.map(it => ({
    id: it.id,
    description: it.description,
    kWh: it.quantityInKWh,
    tariffPerKWh: (it.tariffInCents ?? 0) / 100,
    subtotalInFractionalCurrency: Math.round((it.quantityInKWh || 0) * (it.tariffInCents || 0) * 100)
  }))

  note.totalInFractionalCurrency = backend.totalAmountInCents ?? note.totalInFractionalCurrency
  note.vatInFractionalCurrency = backend.vatAmountInCents ?? note.vatInFractionalCurrency
  note.subTotalInFractionalCurrency = (backend.totalAmountInCents ?? 0) - (backend.vatAmountInCents ?? 0)

  Object.assign(note, calc(note.charges ?? [], note.vatPercentage))
}

export async function getCreditNote(draftId: number) {
  const it = db.get(draftId)
  if (!it) throw new Error('Not found')
  return cloneNote(it)
}

export async function getCreditNoteStable(draftId: number): Promise<CreditNote> {
  const stored = db.get(draftId)
  if (!stored) throw new Error('Not found')

  if (stored.backendId) {
    const backend = await getCreditNoteById(stored.backendId)
    applyBackendToDraft(stored, backend)
    db.set(draftId, stored)
  }

  return cloneNote(stored)
}

export async function createDraftFromSeed(seed: {
  asset: GetAssetDetailsResponse
  customer: CreditNoteCustomer
  vatPercentage: number
}): Promise<CreditNote> {
  const { asset, customer, vatPercentage } = seed

  const id = ++seq
  const totals = calc([], vatPercentage)

  const draft: CreditNote = {
    id,
    creditNoteDateFormat: 'd MMMM yyyy',
    assetCode: asset.assetCode,
    projectCode: asset.projectCode,
    projectName: asset.projectName,
    status: CreditNoteStatus.Draft,
    date: new Date(),
    owner: {
      name: asset.contract.contractFundEntity.name,
      registrationNumber: asset.contract.contractFundEntity.registrationNumber,
      address: asset.contract.contractFundEntity.address,
      vatNumber: asset.contract.contractFundEntity.vatNumber,
      logoUrl: asset.contract.contractFundEntity.logoUrl ?? null,
      accountName: asset.contract.contractFundEntity.accountName,
      bank: asset.contract.contractFundEntity.bank,
      accountNumber: String(asset.contract.contractFundEntity.accountNumber),
      branchCode: asset.contract.contractFundEntity.branchCode
    },
    customer: {
      name: customer.name,
      addressLineOne: customer.addressLineOne,
      addressLineTwo: customer.addressLineTwo,
      suburb: customer.suburb,
      city: customer.city,
      region: customer.region,
      country: customer.country,
      vatNumber: customer.vatNumber
    },
    customerAccountCode: undefined,
    notes: '',
    charges: [],
    vatPercentage,
    ...totals
  }

  db.set(id, draft)
  return cloneNote(draft)
}

export async function createCreditNoteLines(
  draftId: number,
  payload: { charges: CreditNoteCharge[]; vatPercentage: number; notes?: string }
): Promise<CreditNote> {
  const note = await getCreditNote(draftId)

  if (note.backendId) {
    throw new Error('Credit note has already been created in Asset Management.')
  }

  note.vatPercentage = payload.vatPercentage
  if (payload.notes !== undefined) note.notes = payload.notes

  const items = payload.charges.map(c => ({
    description: c.description,
    tariffInCents: Math.round((c.tariffPerKWh ?? 0) * 100),
    quantityInKWh: c.kWh ?? 0
  }))

  const createReq: CreateCreditNoteRequest = {
    assetCode: note.assetCode,
    notes: note.notes ?? '',
    items
  }

  const created = await createCreditNote(createReq)
  applyBackendToDraft(note, created)
  db.set(draftId, note)

  return cloneNote(note)
}

export async function updateCreditNoteLines(
  draftId: number,
  payload: { charges: CreditNoteCharge[]; vatPercentage: number; notes?: string }
): Promise<CreditNote> {
  const note = await getCreditNote(draftId)
  const backendId = note.backendId

  if (!backendId) {
    throw new Error('Cannot update a credit note that has not been created in Asset Management.')
  }

  note.vatPercentage = payload.vatPercentage
  if (payload.notes !== undefined) note.notes = payload.notes

  const updateItems = payload.charges.map(c => ({
    id: c.id ?? 0,
    description: c.description,
    tariffInCents: Math.round((c.tariffPerKWh ?? 0) * 100),
    quantityInKWh: c.kWh ?? 0
  }))

  const updateReq: UpdateCreditNoteRequest = {
    id: backendId,
    notes: note.notes ?? '',
    creditNoteItems: updateItems
  }

  const updated = await updateCreditNote(updateReq)
  applyBackendToDraft(note, updated)
  db.set(draftId, note)

  return cloneNote(note)
}

export async function deleteCreditNoteItemCreated(
  draftId: number,
  lineId: number
): Promise<CreditNote> {
  const note = await getCreditNote(draftId)
  const backendId = note.backendId

  if (!backendId) {
    throw new Error('Cannot delete a line item before the credit note exists in Asset Management.')
  }

  await deleteCreditNoteItemById(backendId, lineId)
  const backend = await getCreditNoteById(backendId)
  applyBackendToDraft(note, backend)
  db.set(draftId, note)

  return cloneNote(note)
}

export async function approveCreditNote(draftId: number): Promise<CreditNote> {
  const note = await getCreditNote(draftId)
  const backendId = note.backendId
  if (!backendId) {
    throw new Error('Cannot approve a credit note that has not been created in Asset Management.')
  }

  const approved = await approveCreditNoteById(backendId)
  applyBackendToDraft(note, approved)
  db.set(draftId, note)

  return cloneNote(note)
}

export async function sendCreditNote(id: number) {
  const note = await getCreditNote(id)
  note.status = CreditNoteStatus.Sent
  db.set(id, note)
  return cloneNote(note)
}

export async function cancelCreditNote(id: number) {
  const note = await getCreditNote(id)
  note.status = CreditNoteStatus.Cancelled
  db.set(id, note)
  return cloneNote(note)
}
